import { useState } from 'react';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { assetsHighlightReelGenerateCreate } from '@goldcast/api/content';
import Button from '@/components/atoms/Button/Button';
import IconButton from '@/components/atoms/Button/IconButton';
import Icon from '@/components/atoms/Icon';
import { classnames } from '@/libs/utils';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';

const TARGET_TIME_RANGES = [30, 60, 90] as const;
const DEFAULT_TARGET_TIME_RANGE = 60;

export default function GenerateHighlightReelPanel({
  onCancel,
  onGenerated
}: {
  onCancel: () => void;
  onGenerated: () => void;
}) {
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const { logger } = useAppContext();
  const [duration, setDuration] = useState<number>(DEFAULT_TARGET_TIME_RANGE);

  const { mutate: generateReel, isLoading: isGenerating } = useMutation(
    () =>
      assetsHighlightReelGenerateCreate({
        body: {
          content_id: broadcastId as string,
          project_id: eventId as string,
          highlight_reel_duration: duration
        }
      }),
    {
      onSuccess: () => {
        showSuccessToast('Highlight reel generation has started. We’ll let you know when it’s ready.');
        onGenerated();
      },
      onError: (err: any) => {
        showErrorToast('Could not start highlight reel generation. Please try again.');
        logger.error('Failed to generate highlight reel', err);
      }
    }
  );

  return (
    <div className="flex flex-col">
      <div className="flex h-14 w-full items-center justify-between p-4 px-5">
        <span className="text-lg font-medium">Generate Highlight Reels</span>
        <IconButton
          icon="IconX"
          title="Close"
          variation="text"
          trackingId="generate-highlight-reel-close-button"
          onClick={onCancel}
        />
      </div>

      <div className="px-5 pb-5">
        <div className="text-xs font-medium text-slate-500">Target time range</div>
        <div className="mt-2 flex w-full items-center gap-1 rounded-lg bg-slate-100 p-1">
          {TARGET_TIME_RANGES.map(range => (
            <button
              key={range}
              type="button"
              data-testid={`highlight-reel-duration-${range}`}
              onClick={() => setDuration(range)}
              className={classnames(
                'flex-1 rounded-md py-1.5 text-xs font-medium transition-all duration-150 ease-in-out',
                duration === range ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              )}
            >
              {range} s
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
        <Button variation="text" trackingId="generate-highlight-reel-cancel-button" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variation="filled"
          trackingId="generate-highlight-reel-submit-button"
          disabled={isGenerating}
          onClick={() => generateReel()}
        >
          <Icon
            name={isGenerating ? 'IconLoader2' : 'IconSparkles'}
            size={14}
            className={classnames('mr-1.5', { 'animate-spin': isGenerating })}
          />
          Generate
        </Button>
      </div>
    </div>
  );
}
